import type { SaleSectionSlug } from '@/saleSections'

export const SALE_SORT_SLUGS = [
  'newest',
  'a-to-z',
  'price-asc',
  'price-desc',
  'biggest-discount',
  'best-sellers',
  'rating',
] as const

export type SaleSortSlug = (typeof SALE_SORT_SLUGS)[number]

export type ParsedSaleSlugs = {
  filters: string[]
  sort: SaleSortSlug
  page: number
  inStockOnly: boolean
  exclusiveOnly: boolean
}

const normalizeSort = (sort: string | undefined): SaleSortSlug => {
  if (sort && (SALE_SORT_SLUGS as readonly string[]).includes(sort)) {
    return sort as SaleSortSlug
  }
  return 'newest'
}

const normalizePage = (page: string | undefined): number => {
  return Math.max(1, Number.parseInt(page ?? '1', 10) || 1)
}

/**
 * Mirrors the category URL parsing convention:
 * - optional filters are leading segments
 * - sort is the 2nd-to-last segment
 * - page is the last segment
 *
 * Also tolerates legacy `/page/{n}` style, where slugs are `['page', '{n}']`.
 */
export const parseSaleSlugs = (slugs?: string[]): ParsedSaleSlugs => {
  const safeSlugs = slugs ?? []

  // Legacy: /sale/{slug}/page/2
  if (safeSlugs.length === 2 && safeSlugs[0] === 'page') {
    return {
      filters: [],
      sort: 'newest',
      page: normalizePage(safeSlugs[1]),
      inStockOnly: false,
      exclusiveOnly: false,
    }
  }

  // Legacy-ish: /sale/{slug}/2
  if (safeSlugs.length === 1 && /^\d+$/.test(safeSlugs[0] ?? '')) {
    return {
      filters: [],
      sort: 'newest',
      page: normalizePage(safeSlugs[0]),
      inStockOnly: false,
      exclusiveOnly: false,
    }
  }

  const filters = safeSlugs.length > 2 ? safeSlugs.slice(0, -2) : []
  const sort = normalizeSort(safeSlugs[safeSlugs.length - 2])
  const page = normalizePage(safeSlugs[safeSlugs.length - 1])

  return {
    filters,
    sort,
    page,
    inStockOnly: filters.includes('in-stock'),
    exclusiveOnly: filters.includes('exclusives'),
  }
}

/**
 * Generates sale URLs matching category structure:
 * `/sale/{saleSlug}/{optional-filters...}/{sort}/{page}/`
 *
 * For the canonical default state (no filters, newest, page 1), returns `/sale/{saleSlug}/`.
 */
export const buildSaleUrl = ({
  saleSlug,
  filters,
  sort,
  page,
}: {
  saleSlug: SaleSectionSlug
  filters: string[]
  sort: string
  page: number
}) => {
  const normalizedSort = normalizeSort(sort)
  const normalizedPage = Math.max(1, page || 1)
  const normalizedFilters = filters.filter(Boolean)

  if (
    normalizedFilters.length === 0 &&
    normalizedSort === 'newest' &&
    normalizedPage === 1
  ) {
    return `/sale/${saleSlug}/`
  }

  const filtersPath =
    normalizedFilters.length > 0 ? `${normalizedFilters.join('/')}/` : ''
  return `/sale/${saleSlug}/${filtersPath}${normalizedSort}/${normalizedPage}/`
}
